/**
 * Custom Customer Reviews Integration
 * Fetches and displays 5-star customer reviews beautifully
 * Clean testimonials without external branding
 */

class CustomerReviews {
  constructor(container) {
    this.container = container;
    this.reviewsToShow = parseInt(container.dataset.reviewCount) || 6;
    this.autoRotate = container.dataset.autoRotate === 'true';
    this.rotateInterval = parseInt(container.dataset.rotateInterval) || 5000;
    this.currentIndex = 0;
    this.reviews = [];
    this.rotateTimer = null;
    
    this.init();
  }

  async init() {
    try {
      await this.fetchReviews();
      if (this.reviews.length > 0) {
        this.render();
        if (this.autoRotate && this.reviews.length > 1) {
          this.startAutoRotate();
        }
      } else {
        this.renderEmpty();
      }
    } catch (error) {
      console.error('Failed to load customer reviews:', error);
      this.renderError();
    }
  }

  async fetchReviews() {
    // First check if we have cached reviews (to avoid repeated fetches)
    const cached = this.getCachedReviews();
    if (cached && cached.length > 0) {
      this.reviews = cached;
      return;
    }

    // Try to fetch live reviews
    try {
      const trustpilotUrl = 'https://dk.trustpilot.com/review/www.el-netsalg.dk';
      
      // Try corsproxy.io - most reliable
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(trustpilotUrl)}`;
      
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml'
        }
      });
      
      if (!response.ok) throw new Error('Fetch failed');
      
      const html = await response.text();
      
      // Parse HTML to extract reviews
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const reviewElements = doc.querySelectorAll('[data-service-review-card-paper]');
      const parsedReviews = [];
      
      reviewElements.forEach(reviewEl => {
        try {
          // Extract star rating
          const ratingEl = reviewEl.querySelector('[data-service-review-rating]');
          const stars = ratingEl ? parseInt(ratingEl.querySelector('img')?.alt?.match(/\d+/)?.[0] || '0') : 0;
          
          // Only include 5-star reviews
          if (stars !== 5) return;
          
          // Extract name
          const nameEl = reviewEl.querySelector('[data-consumer-name-typography]');
          const name = nameEl ? nameEl.textContent.trim() : 'Anonym';
          
          // Extract review text
          const textEl = reviewEl.querySelector('[data-service-review-text-typography]');
          const text = textEl ? textEl.textContent.trim() : '';
          
          // Extract date
          const dateEl = reviewEl.querySelector('time');
          const date = dateEl ? dateEl.getAttribute('datetime') : new Date().toISOString().split('T')[0];
          
          if (text && text.length > 10) {
            parsedReviews.push({ name, stars, text, date, isSample: false });
          }
        } catch (err) {
          console.warn('Error parsing review:', err);
        }
      });
      
      if (parsedReviews.length > 0) {
        // Sort by date - NEWEST FIRST
        parsedReviews.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // Filter: only reviews from last 3 months
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        
        const recentReviews = parsedReviews.filter(review => {
          const reviewDate = new Date(review.date);
          return reviewDate >= threeMonthsAgo;
        });
        
        // Mark reviews older than 1 month to hide date
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        recentReviews.forEach(review => {
          const reviewDate = new Date(review.date);
          review.hideDate = reviewDate < oneMonthAgo;
        });
        
        this.reviews = recentReviews.slice(0, this.reviewsToShow);
        this.cacheReviews(this.reviews);
        console.log('✅ Loaded live reviews:', this.reviews.length);
      } else {
        throw new Error('No reviews parsed');
      }
    } catch (error) {
      console.warn('⚠️ Could not fetch live reviews, using samples:', error);
      // Use stored reviews as fallback
      const storedReviews = this.getStoredReviews();
      this.reviews = storedReviews.length > 0 ? storedReviews : [];
      console.log('📋 Using sample reviews:', this.reviews.length);
    }
  }

  getCachedReviews() {
    try {
      const cached = sessionStorage.getItem('cr_reviews_cache');
      if (!cached) return null;
      
      const { reviews, timestamp } = JSON.parse(cached);
      const cacheAge = Date.now() - timestamp;
      const cacheExpiry = 30 * 60 * 1000; // 30 minutes
      
      if (cacheAge < cacheExpiry) {
        return reviews;
      }
    } catch (e) {
      // Ignore cache errors
    }
    return null;
  }

  cacheReviews(reviews) {
    try {
      sessionStorage.setItem('cr_reviews_cache', JSON.stringify({
        reviews,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }

  getStoredReviews() {
    // Check if reviews are provided via data attribute or global variable
    if (window.customerReviews && Array.isArray(window.customerReviews)) {
      return window.customerReviews.filter(r => r.stars === 5);
    }
    
    // Try to parse from data attribute
    const dataReviews = this.container.dataset.reviews;
    if (dataReviews) {
      try {
        const parsed = JSON.parse(dataReviews);
        return parsed.filter(r => r.stars === 5);
      } catch (e) {
        console.error('Failed to parse reviews data:', e);
      }
    }
    
    return [];
  }

  render() {
    const layout = this.container.dataset.layout || 'grid';
    
    if (layout === 'carousel') {
      this.renderCarousel();
    } else {
      this.renderGrid();
    }
  }

  renderGrid() {
    const reviewsHTML = this.reviews.map(review => this.createReviewCard(review)).join('');
    
    this.container.innerHTML = `
      <div class="cr-reviews__grid">
        ${reviewsHTML}
      </div>
    `;
  }

  renderCarousel() {
    // Group reviews into slides of 3
    const slides = [];
    for (let i = 0; i < this.reviews.length; i += 3) {
      slides.push(this.reviews.slice(i, i + 3));
    }
    
    const slidesHTML = slides.map((slideReviews, slideIndex) => {
      const reviewsHTML = slideReviews.map(review => this.createReviewCard(review)).join('');
      return `
        <div class="cr-reviews__slide ${slideIndex === 0 ? 'is-active' : ''}" data-index="${slideIndex}">
          <div class="cr-reviews__grid">
            ${reviewsHTML}
          </div>
        </div>
      `;
    }).join('');
    
    this.container.innerHTML = `
      <div class="cr-reviews__carousel">
        <div class="cr-reviews__track">
          ${slidesHTML}
        </div>
        ${slides.length > 1 ? `
          <div class="cr-reviews__controls">
            <button class="cr-reviews__nav cr-reviews__nav--prev" aria-label="Forrige anmeldelser">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <div class="cr-reviews__dots"></div>
            <button class="cr-reviews__nav cr-reviews__nav--next" aria-label="Næste anmeldelser">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        ` : ''}
      </div>
    `;
    
    this.totalSlides = slides.length;
    
    if (slides.length > 1) {
      this.setupCarouselControls();
      this.renderDots();
    }
  }

  createReviewCard(review) {
    const stars = this.renderStars(review.stars);
    const excerpt = this.truncateText(review.text, 200);
    const showDate = !review.isSample && review.date && !review.hideDate;
    const date = showDate ? this.formatDate(review.date) : '';
    
    return `
      <div class="cr-review-card">
        <div class="cr-review-card__header">
          <div class="cr-review-card__avatar">
            ${this.getInitials(review.name)}
          </div>
          <div class="cr-review-card__meta">
            <div class="cr-review-card__name">${this.escapeHtml(review.name)}</div>
            <div class="cr-review-card__stars">${stars}</div>
          </div>
        </div>
        <div class="cr-review-card__body">
          <p class="cr-review-card__text">${this.escapeHtml(excerpt)}</p>
          ${review.text.length > 200 ? `<button class="cr-review-card__expand">Læs mere</button>` : ''}
        </div>
        ${showDate ? `
        <div class="cr-review-card__footer">
          <time class="cr-review-card__date" datetime="${review.date}">${date}</time>
        </div>
        ` : ''}
      </div>
    `;
  }

  renderStars(count) {
    const fullStar = `<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/></svg>`;
    return `<span class="cr-stars">${fullStar.repeat(count)}</span>`;
  }

  getInitials(name) {
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('da-DK', options);
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength).trim() + '...';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setupCarouselControls() {
    const prevBtn = this.container.querySelector('.cr-reviews__nav--prev');
    const nextBtn = this.container.querySelector('.cr-reviews__nav--next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.stopAutoRotate();
        this.goToPrevious();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.stopAutoRotate();
        this.goToNext();
      });
    }

    // Swipe support for mobile
    this.setupSwipe();
  }

  setupSwipe() {
    const track = this.container.querySelector('.cr-reviews__track');
    if (!track) return;

    let startX = 0;
    let isDragging = false;

    track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
    });

    track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;

      if (Math.abs(diff) > 50) {
        this.stopAutoRotate();
        if (diff > 0) {
          this.goToNext();
        } else {
          this.goToPrevious();
        }
      }
      isDragging = false;
    });
  }

  renderDots() {
    const dotsContainer = this.container.querySelector('.cr-reviews__dots');
    if (!dotsContainer) return;

    const dots = Array.from({ length: this.totalSlides }, (_, index) => `
      <button class="cr-reviews__dot ${index === 0 ? 'is-active' : ''}" 
              data-index="${index}" 
              aria-label="Gå til slide ${index + 1}">
      </button>
    `).join('');

    dotsContainer.innerHTML = dots;

    dotsContainer.querySelectorAll('.cr-reviews__dot').forEach(dot => {
      dot.addEventListener('click', (e) => {
        this.stopAutoRotate();
        const index = parseInt(e.currentTarget.dataset.index);
        this.goToSlide(index);
      });
    });
  }

  goToNext() {
    this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
    this.updateCarousel();
  }

  goToPrevious() {
    this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.updateCarousel();
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateCarousel();
  }

  updateCarousel() {
    const slides = this.container.querySelectorAll('.cr-reviews__slide');
    const dots = this.container.querySelectorAll('.cr-reviews__dot');

    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.currentIndex);
    });

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
    });
  }

  startAutoRotate() {
    this.rotateTimer = setInterval(() => {
      this.goToNext();
    }, this.rotateInterval);
  }

  stopAutoRotate() {
    if (this.rotateTimer) {
      clearInterval(this.rotateTimer);
      this.rotateTimer = null;
    }
  }

  renderEmpty() {
    this.container.innerHTML = `
      <div class="cr-reviews__empty">
        <p>Ingen kundeanmeldelser tilgængelige endnu.</p>
      </div>
    `;
  }

  renderError() {
    this.container.innerHTML = `
      <div class="cr-reviews__error">
        <p>Kunne ikke indlæse kundeanmeldelser.</p>
      </div>
    `;
  }
}

// Auto-initialize all review containers
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.cr-reviews-container').forEach(container => {
    new CustomerReviews(container);
  });
});

// Expose for manual initialization
window.CustomerReviews = CustomerReviews;
